import { useState } from 'react';
import { Button, Icon, Popover, Select, Text } from '@gravity-ui/uikit';
import { ChevronLeft, ChevronRight } from '@gravity-ui/icons';
import { day, monthTitle } from './format.js';
import { addMonths, monthPeriod, preset, samePeriod, wholeMonth, type Period, type PresetId } from './period.js';
import { DateInput } from './date-input.js';

/**
 * Выбор периода, как в консоли Yandex Cloud: месяц листается стрелками, рядом
 * — быстрые периоды и свой диапазон дат. Один контрол на все отчёты админки.
 */

const PRESETS: { id: PresetId; label: string }[] = [
  { id: 'month', label: 'Этот месяц' },
  { id: 'previous', label: 'Прошлый месяц' },
  { id: 'quarter', label: 'Три месяца' },
  { id: 'year', label: 'Этот год' },
];

function label(period: Period): string {
  const month = wholeMonth(period);
  return month === null ? `${day(period.from)} — ${day(period.to)}` : monthTitle(month);
}

export function PeriodPicker({ value, onChange }: { value: Period; onChange: (period: Period) => void }) {
  const month = wholeMonth(value);
  const active = PRESETS.find((option) => samePeriod(preset(option.id), value))?.id;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1">
        <Button
          view="flat"
          size="m"
          disabled={month === null}
          aria-label="Предыдущий месяц"
          onClick={() => {
            if (month !== null) onChange(monthPeriod(addMonths(month, -1)));
          }}
        >
          <Icon data={ChevronLeft} size={16} />
        </Button>
        <Text variant="subheader-2" className="tabular min-w-36 text-center" whiteSpace="nowrap">
          {label(value)}
        </Text>
        <Button
          view="flat"
          size="m"
          disabled={month === null}
          aria-label="Следующий месяц"
          onClick={() => {
            if (month !== null) onChange(monthPeriod(addMonths(month, 1)));
          }}
        >
          <Icon data={ChevronRight} size={16} />
        </Button>
      </div>

      <Select
        size="m"
        width={160}
        value={active === undefined ? [] : [active]}
        placeholder="Свой период"
        options={PRESETS.map((option) => ({ value: option.id, content: option.label }))}
        onUpdate={(values) => {
          const id = values[0] as PresetId | undefined;
          if (id !== undefined) onChange(preset(id));
        }}
      />

      <CustomRange value={value} onChange={onChange} />
    </div>
  );
}

function CustomRange({ value, onChange }: { value: Period; onChange: (period: Period) => void }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(value.from);
  const [to, setTo] = useState(value.to);
  const invalid = from === '' || to === '' || from > to;

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setFrom(value.from);
          setTo(value.to);
        }
      }}
      placement="bottom-end"
      content={
        <div className="flex w-72 flex-col gap-3 p-4">
          <Text variant="subheader-2">Свой период</Text>
          <DateInput label="С" value={from} onUpdate={setFrom} />
          <DateInput label="По" value={to} onUpdate={setTo} invalid={from !== '' && to !== '' && from > to} />
          {from !== '' && to !== '' && from > to ? (
            <Text variant="caption-2" color="danger">
              Конец раньше начала
            </Text>
          ) : null}
          <Button
            view="action"
            disabled={invalid}
            onClick={() => {
              onChange({ from, to });
              setOpen(false);
            }}
          >
            Показать
          </Button>
        </div>
      }
    >
      <Button view="outlined" size="m">
        Даты…
      </Button>
    </Popover>
  );
}
